import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { inr, pct } from './fmt';
import { TrainerDrilldown } from './TrainerDrilldown';

const Bar = ({ v }) => (
  <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden mt-1">
    <div className={`h-full ${v >= 100 ? 'bg-emerald-500' : v >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, v || 0)}%` }} />
  </div>
);

const MultipleCell = ({ r, quarter, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(r.multiple);
  const [note, setNote] = useState('');
  const save = async reset => {
    try {
      await api.put(`/trainer-targets/quarter/${quarter}/multiple`, { trainer_id: r.trainer_id, multiple: reset ? null : v, note: note || (reset ? 'Back to default' : '') });
      toast.success('Multiple updated'); setEdit(false); setNote(''); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  if (!edit) return (
    <button onClick={() => { setV(r.multiple); setNote(''); setEdit(true); }} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800 hover:text-violet-700" data-testid={`tt-multiple-${r.trainer_id}`}>
      {r.multiple}×{r.overridden && <span className="text-[9px] font-medium text-violet-600 bg-violet-50 px-1 rounded">custom</span>}<Pencil className="w-3 h-3 text-slate-300" />
    </button>
  );
  return (
    <div className="space-y-1 w-44" data-testid={`tt-multiple-edit-${r.trainer_id}`}>
      <Input type="number" step="0.1" value={v} onChange={e => setV(e.target.value)} className="h-7 text-xs" data-testid={`tt-multiple-input-${r.trainer_id}`} />
      <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Reason" className="h-7 text-xs" data-testid={`tt-multiple-note-${r.trainer_id}`} />
      <div className="flex gap-1">
        <Button size="sm" className="h-6 text-[11px] px-2" disabled={!note.trim()} onClick={() => save(false)} data-testid={`tt-multiple-save-${r.trainer_id}`}>Save</Button>
        {r.overridden && <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => save(true)} data-testid={`tt-multiple-reset-${r.trainer_id}`}>Use default</Button>}
        <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={() => setEdit(false)}>Cancel</Button>
      </div>
    </div>
  );
};

const SalaryCell = ({ r, quarter, label, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState('');
  const save = async () => {
    try {
      await api.put(`/trainer-targets/quarter/${quarter}/salary`, { trainer_id: r.trainer_id, monthly_salary: amt });
      toast.success(`Salary saved for ${r.name} from ${label}`); setOpen(false); setAmt(''); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="block ml-auto text-[10px] text-violet-600 hover:underline" data-testid={`tt-set-salary-${r.trainer_id}`}>{r.salary_set ? 'change salary' : 'set salary'}</button>;
  return (
    <div className="flex items-center justify-end gap-1 mt-1" data-testid={`tt-salary-edit-${r.trainer_id}`}>
      <Input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="Monthly ₹" className="h-7 w-24 text-xs" data-testid={`tt-salary-input-${r.trainer_id}`} />
      <Button size="sm" className="h-7 text-[11px] px-2" disabled={!amt} onClick={save} data-testid={`tt-salary-save-q-${r.trainer_id}`}>Save</Button>
      <Button size="sm" variant="ghost" className="h-7 text-[11px] px-1" onClick={() => setOpen(false)}>×</Button>
    </div>
  );
};

export const TargetTable = ({ d, quarter, onSaved, isAdmin }) => {
  const [open, setOpen] = useState(null);
  return (
    <Card className="overflow-x-auto" data-testid="tt-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-2">Trainer</th>
            <th className="text-left px-2">Multiple</th><th className="text-right px-2">Target</th>
            <th className="text-right px-2">Billed</th><th className="text-left px-2">% billed</th>
            <th className="text-right px-2">Collected</th><th className="text-left px-2">% collected</th><th />
          </tr>
        </thead>
        <tbody>
          {d.rows.map(r => (
              <tr key={r.trainer_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`tt-row-${r.trainer_id}`}>
                <td className="px-4 py-2"><button onClick={() => setOpen(r)} className="font-medium text-slate-900 hover:text-violet-700 hover:underline text-left" data-testid={`tt-open-${r.trainer_id}`}>{r.name}</button></td>
                <td className="px-2"><MultipleCell r={r} quarter={quarter} onSaved={onSaved} /></td>
                <td className="text-right px-2 font-semibold" data-testid={`tt-target-${r.trainer_id}`}>{r.target ? inr(r.target) : <span className="text-[11px] text-amber-600 font-normal">Salary not set</span>}
                  {isAdmin && <SalaryCell r={r} quarter={quarter} label={d.quarter.label} onSaved={onSaved} />}</td>
                <td className="text-right px-2" data-testid={`tt-billed-${r.trainer_id}`}>{inr(r.billed)}</td>
                <td className="px-2 text-xs">{pct(r.billed_pct)}{r.billed_pct !== null && <Bar v={r.billed_pct} />}</td>
                <td className="text-right px-2" data-testid={`tt-collected-${r.trainer_id}`}>{inr(r.collected)}</td>
                <td className="px-2 text-xs">{pct(r.collected_pct)}{r.collected_pct !== null && <Bar v={r.collected_pct} />}</td>
                <td className="px-2"><button onClick={() => setOpen(r)} data-testid={`tt-expand-${r.trainer_id}`} className="text-slate-400 hover:text-violet-700"><ChevronRight className="w-4 h-4" /></button></td>
              </tr>
          ))}
        </tbody>
      </table>
      {open && <TrainerDrilldown quarter={quarter} trainer={open} onClose={() => setOpen(null)} />}
    </Card>
  );
};
